import { create } from "zustand";
import type { Theme } from "../types/theme.types.js";

// Dark mode. Os tokens.dark e o Tailwind darkMode:["class"] já existem;
// este store é só o mecanismo de ativação: classe `dark` na raiz + persistência local +
// respeito ao SO em modo 'system'. O anti-flash inicial vive no script inline do index.html.
const STORAGE_KEY = "opus-dt-theme";

// matchMedia pode não existir (jsdom/SSR) — guarda defensiva.
function systemMql(): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;
}

function effectiveIsDark(theme: Theme): boolean {
  return theme === "dark" || (theme === "system" && (systemMql()?.matches ?? false));
}

function applyClass(theme: Theme): boolean {
  const dark = effectiveIsDark(theme);
  document.documentElement.classList.toggle("dark", dark);
  return dark;
}

function readStored(): Theme {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

interface ThemeState {
  theme: Theme;
  isDark: boolean; // tema EFETIVO (reativo mesmo em modo 'system')
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const initialTheme = readStored();

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initialTheme,
  isDark: applyClass(initialTheme), // reconcilia a classe no boot (idempotente com o anti-flash)
  setTheme: (theme) => {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, theme);
    set({ theme, isDark: applyClass(theme) });
  },
  toggle: () => get().setTheme(get().isDark ? "light" : "dark"),
}));

// Listener do SO registrado UMA vez no escopo do módulo (não no initializer do store, que poderia
// re-rodar). Reaplica só em modo 'system'. HMR-safe: dispõe o listener no hot-update (sem leak/dup).
const mql = systemMql();
if (mql) {
  const onSystemChange = () => {
    if (useThemeStore.getState().theme === "system") {
      useThemeStore.setState({ isDark: applyClass("system") });
    }
  };
  mql.addEventListener("change", onSystemChange);
  const hot = (import.meta as { hot?: { dispose: (cb: () => void) => void } }).hot;
  if (hot) hot.dispose(() => mql.removeEventListener("change", onSystemChange));
}
